import React from 'react'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import { FormEventHandler, useMemo } from 'react'
import log from 'loglevel'
import {
  DeleteAuditRequest,
  AuditRecord,
} from '@trustero/trustero-api-web/lib/audit/audit_pb'
import styled from 'styled-components/macro'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useCurrentFormAuditId } from 'src/context/FormContext/CurrentFormAuditContext'
import { ModalForm, ModalFormId, ModalFormIdQueryParam } from '../ModalForm'
import { useAuthorizedGrpcClient } from '../../../adapter'
import { useAudit } from '../../async/model/audit'
import { useGrpcRevalidateByMethod } from '../../async'
import { useDocumentRequestCount } from '../../async/DocumentRequest/useDocumentRequests'
import { Spinner } from '../../../Throbber/Spinner'
import { TestIds } from '../../../Utils/testIds'

export const DeleteAuditQueryParams = {
  MODAL_FORM_ID: ModalFormIdQueryParam,
  AUDIT_ID: 'auditId',
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`

export const DeleteAuditConfirmation = (): JSX.Element => {
  const { auditId } = useCurrentFormAuditId()
  if (!auditId) {
    return <></>
  }
  return <DeleteAuditConfirmationContent auditId={auditId} />
}

const DeleteAuditConfirmationContent = ({
  auditId,
}: {
  auditId: string
}): JSX.Element => {
  const auditClient = useAuthorizedGrpcClient(AuditPromiseClient)
  const grpcRevalidateByMethod = useGrpcRevalidateByMethod()
  const { data: audit } = useAudit(auditId)
  const { loading, data: requestCountRes } = useDocumentRequestCount(auditId)
  const requestCount = requestCountRes?.getCount() || 0

  const show = useIsShowModal(ModalFormId.DELETE_AUDIT)
  const hide = useHideModal({ modalId: ModalFormId.DELETE_AUDIT })

  const onSubmit: FormEventHandler<HTMLFormElement> = useMemo(() => {
    return async (e) => {
      e.preventDefault()

      try {
        await deleteAudit(auditClient, auditId)
        await grpcRevalidateByMethod(AuditPromiseClient.prototype.getAudits)
        hide()
      } catch (err) {
        log.error('Error in DeleteAuditConfirmation', err)
      }
    }
  }, [auditClient, auditId, grpcRevalidateByMethod, hide])

  return (
    <ModalForm
      show={show}
      hide={hide}
      formId={ModalFormId.DELETE_AUDIT}
      title={'Are you sure you want to delete the audit?'}
      size={'lg'}
      submitText={'Delete Audit'}
    >
      {!loading ? (
        <form id={ModalFormId.DELETE_AUDIT} onSubmit={onSubmit}>
          {audit?.getName()} will be deleted completely.
          <ul>
            <li>
              This will delete all associated document requests. There are{' '}
              {requestCount}.
            </li>
            <li>
              This <i>won’t affect</i> your policies, controls, evidence, and
              test results.
            </li>
          </ul>
        </form>
      ) : (
        <LoadingContainer>
          <Spinner data-testid={TestIds.SPINNER} color="primary" size="l" />
        </LoadingContainer>
      )}
    </ModalForm>
  )
}

const deleteAudit = async (
  client: AuditPromiseClient,
  auditId: string,
): Promise<AuditRecord> => {
  const deleteAuditRequest = new DeleteAuditRequest().setAuditId(auditId)
  try {
    await client.deleteAudit(deleteAuditRequest)
  } catch (err) {
    log.error('failed to delete audit: ', err)
  }
  return new AuditRecord()
}
